import { forwardRef, ForwardRefRenderFunction, useEffect } from "react";
//#region types
import { WintSalaryEnums } from "models/enums";
import { SalaryDeviationFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { WintDeviationFormProps } from "models/propTypes/wintSalaryPropTypes";
import { ChoiceProps, SearchInputProps, TextInputProps, ToggleProps } from "models/propTypes/wintFormPropsTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import {
  Choice,
  HorizontalFormField,
  SearchInput,
  TextInput,
  Toggle,
  useWintForm,
  VerticalFormField,
  WintForm,
} from "components/common/wintForms";
import { StaticInfoMessage } from "components/common";
import TimeDeviationSection from "./timeDeviationSection";
import AmountSection from "./amountSection";
import TravelCompensationSection from "./travelCompensationSection";
import TimeBalancesSection from "./timeBalancesSection";
import WithdrawalSection from "./withdrawalSection";
import FuelBenefitSection from "./fuelBenefitSection";
import WintDeviationSelection from "./wintDeviationSelection";
import useSalaryDeviationValues from "../useSalaryDeviationValues";
import { useWintSalaryService } from "serviceHooks";
import { useDeviationPermissions } from "components/salaryDeviation";
import SalaryDeviationHelper from "./salaryDeviationHelper";
import useChildCreateDialog from "components/wintSalary/useChildCreateDialog";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
import { LocaleEnums } from "models/enums";

const WintDeviationCreateForm: ForwardRefRenderFunction<FormHandles, WintDeviationFormProps> = (props, ref) => {
  const {
    defaultValues,
    validationSchema,
    handleSubmit,
    availableDeviationTypes,
    availableDeviationReportTypes,
    allDeviationTypes,
    typesLoading,
    editMode,
    readOnly,
    lockedPeriod,
    allDayHoursRef,
    onFormUpdate,
    showInfoMessage,
    availableChildren,
    onChildCreated,
    withdrawalBalances,
    withdrawalBalancesLoading,
    withdrawalBalancesFailed,
    withdrawalCap,
  } = props;

  const { searchPersonsAutoComplete } = useWintSalaryService();
  const { getResetedValues } = useSalaryDeviationValues();

  const { isSalaryAdmin, isBusinessUnitManagerWithSalaryRights, isDeviationAdmin, canManageSalary } =
    useDeviationPermissions();

  const { wintFormProps, connect, values, setValues } = useWintForm<SalaryDeviationFormTypes.WintDeviationCreate>({
    formRef: ref,
    defaultValues: defaultValues,
    validationSchema: validationSchema,
    handleSubmit: handleSubmit,
    enableReinitialize: true,
    onFormUpdate: onFormUpdate,
  });

  const deviationTypeConnector = connect("deviationType");

  useEffect(() => {
    if (editMode) return;
    if (!values.deviation?.id) return;
    const stillAvailable = allDeviationTypes.some((t) => t.id === values.deviation?.id);
    if (!stillAvailable) {
      setValues(getResetedValues(values));
    }
    // only re-run when the scoped list changes, not on every value edit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDeviationTypes]);

  const { showChildCreateDialog } = useChildCreateDialog();

  const onChildAdd = (_value?: string): Promise<AutocompleteListItem | undefined> => {
    const personId = values.employee?.id;
    if (!personId) return Promise.resolve(undefined);

    return new Promise((resolve) => {
      showChildCreateDialog(
        Number(personId),
        (child) => {
          const item = onChildCreated?.(child);
          resolve(item);
        },
        () => resolve(undefined)
      );
    });
  };

  const onDeviationTypeChange = (selectedType?: WintSalaryEnums.SalaryDeviationType) => {
    const newValues: SalaryDeviationFormTypes.WintDeviationCreate = getResetedValues(values);
    newValues.deviationType = selectedType;
    setValues(newValues);
  };

  const onDeviationChange = (newDeviation: AutocompleteListItem | undefined, shouldValidate?: boolean) => {
    const newValues: SalaryDeviationFormTypes.WintDeviationCreate = {
      ...values,
      deviation: newDeviation,
    };

    // Child selection only applies to parental leave deviations - clear it when switching
    // to any other classification so a stale childId is never submitted
    if (!SalaryDeviationHelper.isParentalLeaveClassification(newDeviation)) {
      newValues.child = undefined;
    }

    // Same reason, for the placement opt-in: every eligible type sits in the Absence category, so
    // switching between absence types comes through here and never through getResetedValues. A tick
    // left standing would submit on a type the salary service rejects, with the control hidden.
    if (!SalaryDeviationHelper.allowsCurrentMonthPlacementChoice(newDeviation)) {
      newValues.useCurrentMonthPlacement = false;
    }

    // Vacation deviations are always full days - reset any time reporting state
    // carried over from a previously selected deviation
    if (SalaryDeviationHelper.isVacationClassification(newDeviation)) {
      newValues.timeDeviation = {
        ...values.timeDeviation,
        reportType: WintSalaryEnums.SalaryDeviationReportType.FullDay,
        fillDayHours: false,
        dayHours: [],
        time: undefined,
      };
    }

    setValues(newValues, shouldValidate);
  };

  const onEmployeeChange = (newEmployee: AutocompleteListItem | undefined, shouldValidate?: boolean) => {
    setValues(
      {
        ...values,
        employee: newEmployee,
        child: undefined,
      },
      shouldValidate
    );
  };

  const searchUsers = (searchStr: string) =>
    searchPersonsAutoComplete(
      searchStr,
      false,
      true,
      isBusinessUnitManagerWithSalaryRights && !(isSalaryAdmin || isDeviationAdmin)
    );

  const renderDeviationSelection = () => {
    if (values.deviationType === undefined) return undefined;

    return (
      <WintDeviationSelection
        connect={connect}
        selectedDeviationType={values.deviationType}
        allDeviationTypes={allDeviationTypes}
        editMode={editMode}
        onDeviationChange={onDeviationChange}
      />
    );
  };

  const currentLanguage = useSelector((store: AppState) => store.common.locale.language);

  const getLocalizedCategoryName = () => {
    const localizedUnit = values.deviation?.data?.localizedInformationTypeUnit;
    if (!localizedUnit) {
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Amount);
    }

    const name = currentLanguage === LocaleEnums.LocaleType.sv ? localizedUnit.nameSv : localizedUnit.nameEn;
    return name ?? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Amount);
  };

  const renderDeviationForm = () => {
    if (values.deviationType === undefined || !values.deviation) return undefined;

    const showTimeDeviation = SalaryDeviationHelper.shouldShowTimeDeviationSection(
      values.deviationType,
      values.deviation
    );
    const showAmount = SalaryDeviationHelper.shouldShowAmountSection(values.deviationType, values.deviation);
    const showTravelCompensation = SalaryDeviationHelper.shouldShowTravelCompensationSection(values.deviation);
    const showTimeBalances = SalaryDeviationHelper.shouldShowTimeBalancesSection(
      values.deviationType,
      values.deviation
    );
    const showWithdrawal = SalaryDeviationHelper.shouldShowWithdrawalSection(values.deviationType, values.deviation);
    const showComment = SalaryDeviationHelper.shouldShowCommentSection(values.deviation);
    const showFuelBenefit = SalaryDeviationHelper.isFuelBenefit(values.deviation);
    // Shown while editing too, but disabled: the flag rides only on the create DTO, so the mode is
    // fixed once registered — rendering it read-only is what tells the user which month it pays on.
    // Kept for an already opted-in deviation even if its type has since been set back to Never, so
    // the mode never silently disappears from a deviation that still carries it.
    const showCurrentMonthPlacement =
      canManageSalary &&
      (SalaryDeviationHelper.allowsCurrentMonthPlacementChoice(values.deviation) ||
        (editMode && values.useCurrentMonthPlacement === true));
    const showChildSelection = SalaryDeviationHelper.isParentalLeaveClassification(values.deviation);

    return (
      <>
        {showChildSelection && (
          <VerticalFormField<SearchInputProps>
            Component={SearchInput}
            {...connect("child")}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.Child)}
            placeholder={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.SalaryDeviation.ChildPlaceholder
            )}
            items={() => Promise.resolve(availableChildren ?? [])}
            icon={IconDefinitionConstants.WChild}
            disabled={readOnly}
            actions={[
              {
                id: "ADD_CHILD_ACTION",
                value: LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.SalaryDeviation.CreateChildDialogTitle
                ),
                icon: IconDefinitionConstants.WPlus,
                onActionClicked: onChildAdd,
                actionSuccessMessage: LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.SalaryDeviation.ChildCreatedSuccess
                ),
                hide: () => !values.employee?.id,
              },
            ]}
          />
        )}
        {showTimeDeviation && (
          <TimeDeviationSection
            connect={connect}
            availableDeviationReportTypes={availableDeviationReportTypes}
            values={values}
            editMode={editMode}
            readOnly={readOnly}
            allDayHoursRef={allDayHoursRef}
            // The re-approval warning is about the consequences of editing — suppress it when
            // nothing can be edited, so the read-only message is the only banner in the form.
            showInfoMessage={showInfoMessage && !lockedPeriod}
            canManageSalary={canManageSalary}
          />
        )}
        {showAmount && (
          <AmountSection connect={connect} values={values} labelText={getLocalizedCategoryName()} readOnly={readOnly} />
        )}
        {showWithdrawal && canManageSalary && (
          <WithdrawalSection
            connect={connect}
            classification={SalaryDeviationHelper.getWithdrawalClassification(values.deviation)}
            balances={withdrawalBalances}
            balancesLoading={withdrawalBalancesLoading}
            balancesFailed={withdrawalBalancesFailed}
            cap={withdrawalCap}
            editMode={editMode}
            readOnly={readOnly}
          />
        )}
        {showFuelBenefit && <FuelBenefitSection connect={connect} values={values} readOnly={readOnly} />}
        {showTravelCompensation && <TravelCompensationSection connect={connect} values={values} readOnly={readOnly} />}
        {showTimeBalances && <TimeBalancesSection connect={connect} readOnly={readOnly} />}
        {showCurrentMonthPlacement && (
          <HorizontalFormField<ToggleProps>
            Component={Toggle}
            {...connect("useCurrentMonthPlacement")}
            label={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.SalaryDeviation.UseCurrentMonthPlacement
            )}
            descriptionText={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.SalaryDeviation.UseCurrentMonthPlacementHelpText
            )}
            mainLabelGridSize={8}
            childGridSize={3}
            disabled={readOnly || editMode}
          />
        )}
        {showComment && (
          <VerticalFormField<TextInputProps>
            Component={TextInput}
            {...connect("comment")}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.OtherInformation)}
            icon={IconDefinitionConstants.WCommentDots}
            showMainLabel
            descriptionText={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.SalaryDeviation.CompensationCommentDescription
            )}
            disabled={readOnly}
          />
        )}
      </>
    );
  };

  return (
    <WintForm {...wintFormProps}>
      {lockedPeriod && (
        <StaticInfoMessage gutterBottom>
          {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.ReadOnlyLockedPeriodMessage)}
        </StaticInfoMessage>
      )}
      {canManageSalary && (
        <VerticalFormField<SearchInputProps>
          Component={SearchInput}
          {...connect("employee")}
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.Employee)}
          items={searchUsers}
          icon={IconDefinitionConstants.WUser}
          disabled={editMode}
          setValue={onEmployeeChange}
        />
      )}
      <VerticalFormField<ChoiceProps>
        Component={Choice}
        {...deviationTypeConnector}
        label={TranslationKeys.SalaryDeviation.DeviationTypeLabel}
        showMainLabel
        items={availableDeviationTypes}
        setValue={onDeviationTypeChange}
        disabled={editMode}
        isLoading={typesLoading}
      />
      {renderDeviationSelection()}
      {renderDeviationForm()}
    </WintForm>
  );
};

export default forwardRef(WintDeviationCreateForm);
